import OrderDTO from "@/dto/order/OrderDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import {
    AddressDeliverable,
    AddressDeliverableCheckResult,
    DyAddressDeliverableOrder,
    TbDeliverableCheckAddress,
    getDyAddressDeliverableCheck,
    getTbAddressDeliverableCheck,
} from "@/services/api/addressDeliverableCheck";
import { hasLocalConsigneeInfo } from "@/utils/OrderUtils";
import StringUtils from "@/utils/StringUtils";
import to from "@/utils/to";
import { chunk, groupBy } from "lodash";
import { computed, observable, makeObservable, actionBound } from "mobx";
import { createContext, useContext } from "react";
import GlobalStore from "@/store";
import ShopType from "@/enum/ShopType";
import { AxiosPlatformType } from "@/services/http/platformServer";

function buildAddress(order: OrderDTO): TbDeliverableCheckAddress {
    const address: any = {};
    const hasLocalConsignee = hasLocalConsigneeInfo(order.consignee);

    if (hasLocalConsignee) {
        address.province = order.consignee.localProvince;
        address.city = order.consignee.localCity;
        address.district = order.consignee.localDistrict;
        address.street = order.consignee.localStreet;
        address.fullAddress = StringUtils.join(
            order.consignee.localProvince,
            order.consignee.localCity,
            order.consignee.localDistrict,
            order.consignee.localStreet,
        );
    } else {
        address.province = order.consignee.province;
        address.city = order.consignee.city;
        address.district = order.consignee.district;
        address.street = order.consignee.street;
        address.fullAddress = StringUtils.join(
            order.consignee.province,
            order.consignee.city,
            order.consignee.district,
            order.consignee.street,
        );
    }

    address.orderId = order.id;

    return address;
}

/** 如果检测的店铺是抖店且是以下的快递公司时，调用抖店的检测接口 */
const callDyApiLogisticsCompanyList = [
    LogisticsCompany.YTO,
    LogisticsCompany.ZTO,
    LogisticsCompany.YUNDA,
    LogisticsCompany.SF,
    LogisticsCompany.JD_EXPRESS,
    LogisticsCompany.STO,
    LogisticsCompany.EMS,
    LogisticsCompany.POSTB,
    LogisticsCompany.JT,
];
class AddressDeliverableStore {
    constructor(public globalStore: typeof GlobalStore, public forAllShop: boolean) {
        makeObservable(this, {
            loading: observable,
            addressDeliverableCheckResults: observable,
            orderDeliverableMap: computed,
            getAddressDeliverable: actionBound,
            clearAddressDeliverableCheckResults: actionBound,
        });
    }

    loading = false;

    addressDeliverableCheckResults: AddressDeliverableCheckResult[] = [];

    get orderDeliverableMap() {
        const map = new Map<number, AddressDeliverable>();

        this.addressDeliverableCheckResults.forEach(({ orderId, deliverable }) => {
            map.set(orderId, deliverable);
        });

        return map;
    }

    async getAddressDeliverable(orders: OrderDTO[], logisticsCompany: LogisticsCompany) {
        this.loading = true;

        const groupOrders = groupBy(orders, ({ shopId }) =>
            this.globalStore.shopMapping.get(shopId)?.type === ShopType.DOUDIAN
                ? AxiosPlatformType.DOUDIAN
                : AxiosPlatformType.ALIYUN,
        );
        const results = [];

        await this.globalStore.waybillPrintStore.loadShippingAddresses();

        const { shippingAddresses } = this.globalStore.waybillPrintStore;

        for (const key in groupOrders) {
            const chunkOrders = chunk(groupOrders[key], 50);

            for (let i = 0; i < chunkOrders.length; i++) {
                const chunk = chunkOrders[i];

                /** 店铺为抖店且是指定快递公司，使用抖店的检测接口 */
                if (
                    !this.forAllShop &&
                    Number(key) === AxiosPlatformType.DOUDIAN &&
                    shippingAddresses.length &&
                    callDyApiLogisticsCompanyList.includes(logisticsCompany)
                ) {
                    const shopId = chunk[0].shopId;
                    const orderParams: DyAddressDeliverableOrder[] = chunk.map(
                        ({ id, mergeSplitStatus, originalId }) => ({ id, mergeSplitStatus, originalId }),
                    );

                    const shippingAddress =
                        shippingAddresses.find(({ bindingShopIds }) => bindingShopIds?.includes(String(shopId))) ||
                        shippingAddresses[0];

                    const [, res] = await to(
                        getDyAddressDeliverableCheck({
                            shopId,
                            logisticsCompany,
                            orders: orderParams,
                            shipperAddress: {
                                province: shippingAddress.province,
                                city: shippingAddress.city,
                                district: shippingAddress.district,
                                street: shippingAddress.street,
                            },
                        }),
                    );

                    if (res) {
                        results.push(...res);
                    }
                } else {
                    const addresses = chunk.map(buildAddress);
                    const [, res] = await to(
                        getTbAddressDeliverableCheck({ logisticsCompany, addressesDtos: addresses }),
                    );

                    if (res) {
                        results.push(...res);
                    }
                }
            }
        }

        this.addressDeliverableCheckResults = results;

        this.loading = false;
    }

    clearAddressDeliverableCheckResults() {
        this.addressDeliverableCheckResults = [];
    }
}

export const AddressDeliverableStoreContext = createContext<AddressDeliverableStore>(undefined);

export const useAddressDeliverableStore = () => {
    return useContext(AddressDeliverableStoreContext);
};

export default AddressDeliverableStore;
